<template>
  <div class="crud_form_wrapper single_show_content_wrapper">
    <div class="form_title_wrapper">
      <h4>{{ $t("PLACEHOLDERS.showTask") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #26a1d6">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>

    <div class="single_step_form_content_wrapper">
      <div class="badges_wrapper justify-content-end">
        <v-chip :color="data.is_active ? 'green' : 'red'" text-color="white">
          {{ data.is_active ? $t("STATUS.active") : $t("STATUS.notActive") }}
        </v-chip>
      </div>

      <form>
        <div class="row">
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.taskNameAr')"
            v-model.trim="data.name_ar"
            disabled
          />
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.taskNameEn')"
            v-model.trim="data.name_en"
            disabled
          />
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.startDate')"
            v-model.trim="data.start_at"
            disabled
          />
          <base-input
            col="6"
            type="text"
            :placeholder="$t('PLACEHOLDERS.endDate')"
            v-model.trim="data.end_at"
            disabled
          />
          <base-input
            col="12"
            type="textarea"
            :placeholder="$t('PLACEHOLDERS.taskInstructions')"
            v-model.trim="data.instructions"
            disabled
          />
        </div>
      </form>

      <div class="mt-4">
        <h6 class="mb-2">{{ $t("PLACEHOLDERS.facilityName") }}</h6>
        <div class="facility_box" v-if="data.facility">
          <p>
            <strong>{{ $t("PLACEHOLDERS.facilityName") }}:</strong>
            {{ data.facility.name }}
          </p>
          <p>
            <strong>{{ $t("PLACEHOLDERS.address") }}:</strong>
            {{ data.facility.address }}
          </p>
        </div>

        <div class="mt-4 live_tracking_section">
          <h6 class="mb-2">{{ $t("PLACEHOLDERS.liveEmployeeTracking") }}</h6>
          <div class="live_tracking_map">
            <v-chip
              class="live_connection_badge"
              :color="isPusherConnected ? 'green' : 'grey'"
              text-color="white"
              small
            >
              {{ connectionStatusLabel }}
            </v-chip>
            <ChangableMapComponent
              :lat="facilityMapCenter.lat"
              :lng="facilityMapCenter.lng"
              selection-mode="points"
              :points="facilityShape"
              :live-markers="liveMapMarkers"
              :interactive="false"
              :auto-fit-bounds="true"
              :zoom="6"
              @update:lat="() => {}"
              @update:lng="() => {}"
            />
          </div>
          <p v-if="!liveMapMarkers.length" class="live_tracking_empty">
            {{ $t("PLACEHOLDERS.noLiveLocations") }}
          </p>
          <div v-else class="live_guards_list">
            <v-chip
              v-for="guard in liveMapMarkers"
              :key="`guard-chip-${guard.id}`"
              small
              class="ma-1"
              :color="
                guard.is_inside === true
                  ? 'green'
                  : guard.is_inside === false
                  ? 'red'
                  : 'blue'
              "
              text-color="white"
            >
              {{ guard.name }}
            </v-chip>
          </div>
        </div>
      </div>

      <div class="mt-4">
        <h6 class="mb-2">{{ $t("PLACEHOLDERS.taskEmployeeSchedule") }}</h6>
        <v-data-table
          :headers="employeeHeaders"
          :items="employeesRows"
          hide-default-footer
          item-class="ltr"
          class="thumb"
        >
          <template v-slot:no-data>
            {{ $t("TABLES.noData") }}
          </template>

          <template v-slot:[`item.name`]="{ item }">
            <span>{{ item.name || $t("TABLES.noData") }}</span>
          </template>

          <template v-slot:[`item.work_start_time`]="{ item }">
            <span>{{ formatWorkStartTime(item) }}</span>
          </template>

          <template v-slot:[`item.work_end_time`]="{ item }">
            <span>{{ formatWorkEndTime(item) }}</span>
          </template>

          <template v-slot:[`item.break_start_time`]="{ item }">
            <span>{{ formatBreakStartTime(item) }}</span>
          </template>

          <template v-slot:[`item.break_end_time`]="{ item }">
            <span>{{ formatBreakEndTime(item) }}</span>
          </template>

          <template v-slot:[`item.reports`]="{ item }">
            <div class="actions">
              <a-tooltip placement="bottom" v-if="item.reports?.length">
                <template slot="title">
                  <span>{{ $t("PLACEHOLDERS.viewReports") }}</span>
                </template>
                <button
                  type="button"
                  class="btn_edit"
                  @click="openReportsModal(item)"
                >
                  <i class="fas fa-file-alt"></i>
                </button>
              </a-tooltip>
              <span v-else>-</span>
            </div>
          </template>

          <template v-slot:[`item.rate`]="{ item }">
            <span>{{ item.rate || $t("TABLES.noData") }}</span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="actions">
              <a-tooltip
                placement="bottom"
                v-if="!isEmployeeTaskFinished(item)"
              >
                <template slot="title"
                  ><span>{{ $t("PLACEHOLDERS.finishTask") }}</span></template
                >
                <button
                  class="btn_show"
                  @click="changeEmployeeTaskStatus(item, 'finished')"
                >
                  <i class="fas fa-check"></i>
                </button>
              </a-tooltip>

              <a-tooltip
                placement="bottom"
                v-if="!isEmployeeTaskFinished(item)"
              >
                <template slot="title"
                  ><span>{{ $t("PLACEHOLDERS.rateEmployee") }}</span></template
                >
                <button class="btn_edit" @click="openRateModal(item)">
                  <i class="fas fa-star"></i>
                </button>
              </a-tooltip>

              <p v-if="isEmployeeTaskFinished(item)">-</p>
            </div>
          </template>
        </v-data-table>
      </div>

      <div class="mt-4">
        <div class="attendance_section_header">
          <h6 class="mb-0">{{ $t("PLACEHOLDERS.attendanceRecord") }}</h6>
          <button
            type="button"
            class="add_attendance_btn"
            @click="openAttendanceModal"
          >
            {{ $t("PLACEHOLDERS.addAttendanceRecord") }}
          </button>
        </div>

        <v-data-table
          :headers="attendanceHeaders"
          :items="attendanceRows"
          hide-default-footer
          item-class="ltr"
          class="thumb mt-2"
        >
          <template v-slot:no-data>
            {{ $t("TABLES.noData") }}
          </template>

          <template v-slot:[`item.user.name`]="{ item }">
            <span>{{ item.user?.name || $t("TABLES.noData") }}</span>
          </template>

          <template v-slot:[`item.date`]="{ item }">
            <span>{{ item.date || $t("TABLES.noData") }}</span>
          </template>

          <template v-slot:[`item.check_in`]="{ item }">
            <span>{{ formatScheduleTime(item.check_in) }}</span>
          </template>

          <template v-slot:[`item.check_out`]="{ item }">
            <span>{{ formatScheduleTime(item.check_out) }}</span>
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <span>{{ item.status || $t("TABLES.noData") }}</span>
          </template>

          <template v-slot:[`item.expected_work_start_time`]="{ item }">
            <span>{{ formatWorkStartTime(item) }}</span>
          </template>

          <template v-slot:[`item.expected_work_end_time`]="{ item }">
            <span>{{ formatWorkEndTime(item) }}</span>
          </template>

          <template v-slot:[`item.expected_break_start_time`]="{ item }">
            <span>{{ formatBreakStartTime(item) }}</span>
          </template>

          <template v-slot:[`item.expected_break_end_time`]="{ item }">
            <span>{{ formatBreakEndTime(item) }}</span>
          </template>
        </v-data-table>
      </div>

      <v-dialog v-model="reportsDialog" max-width="700px">
        <v-card>
          <v-card-title class="text-h5 justify-center">
            {{ $t("PLACEHOLDERS.employeeReports") }}
            <span v-if="selectedEmployeeReports?.name">
              - {{ selectedEmployeeReports.name }}
            </span>
          </v-card-title>

          <div class="w-100 px-4 pb-4">
            <div
              v-if="!selectedEmployeeReports?.reports?.length"
              class="text-center py-4"
            >
              {{ $t("MESSAGES.noReportsAvailable") }}
            </div>

            <div
              v-for="report in selectedEmployeeReports?.reports || []"
              :key="report.id"
              class="report_item"
            >
              <p class="report_meta">
                <strong>{{ $t("PLACEHOLDERS.reportType") }}:</strong>
                {{ report.type || $t("TABLES.noData") }}
              </p>

              <p v-if="report.text" class="report_meta">
                <strong>{{ $t("PLACEHOLDERS.reportText") }}:</strong>
                {{ report.text }}
              </p>

              <div v-if="report.files?.length" class="report_files">
                <a
                  v-for="file in report.files"
                  :key="file.id"
                  :href="file.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="report_file_link"
                >
                  <img
                    v-if="isImageFile(file.url) || report.type === 'image'"
                    :src="file.url"
                    :alt="report.text || $t('PLACEHOLDERS.employeeReports')"
                    class="report_image"
                  />
                  <span v-else>{{ file.url }}</span>
                </a>
              </div>
            </div>
          </div>

          <v-card-actions>
            <v-btn class="modal_cancel_btn" @click="reportsDialog = false">
              {{ $t("BUTTONS.close") }}
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="attendanceDialog"
        max-width="600px"
        content-class="attendance_dialog"
      >
        <v-card>
          <v-card-title class="text-h5 justify-center">
            {{ $t("PLACEHOLDERS.addAttendanceRecord") }}
          </v-card-title>

          <form class="w-100 px-4" @submit.prevent="submitAttendanceChange">
            <div class="row">
              <base-select-input
                col="12"
                :optionsList="attendanceEmployeeOptions"
                :placeholder="$t('PLACEHOLDERS.employeeName')"
                v-model="attendanceForm.employee"
                required
              />

              <base-picker-input
                col="12"
                type="date"
                :placeholder="$t('PLACEHOLDERS.date')"
                v-model="attendanceForm.date"
                :getPopupContainer="getAttendancePopupContainer"
                required
              />

              <base-select-input
                col="12"
                :optionsList="attendanceStatuses"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="attendanceForm.status"
                required
              />

              <base-picker-input
                col="12"
                type="time"
                :placeholder="$t('PLACEHOLDERS.checkInTime')"
                v-model="attendanceForm.check_in"
              />

              <base-picker-input
                col="12"
                type="time"
                :placeholder="$t('PLACEHOLDERS.checkOutTime')"
                v-model="attendanceForm.check_out"
              />
            </div>
          </form>

          <v-card-actions>
            <v-btn
              class="modal_confirm_btn"
              :loading="isWaitingAttendanceRequest"
              :disabled="isWaitingAttendanceRequest"
              @click="submitAttendanceChange"
            >
              {{ $t("BUTTONS.save") }}
            </v-btn>
            <v-btn class="modal_cancel_btn" @click="closeAttendanceModal">
              {{ $t("BUTTONS.cancel") }}
            </v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="rateDialog" max-width="600px">
        <v-card>
          <v-card-title class="text-h5 justify-center">
            {{ $t("PLACEHOLDERS.rateEmployee") }}
          </v-card-title>

          <form class="w-100 px-4" @submit.prevent="submitRate">
            <div class="row">
              <base-input
                col="12"
                type="number"
                :placeholder="$t('PLACEHOLDERS.points')"
                v-model="rateData.points"
              />

              <base-input
                col="12"
                rows="3"
                type="textarea"
                :placeholder="$t('PLACEHOLDERS.comment')"
                v-model="rateData.comment"
              />
            </div>
          </form>

          <v-card-actions>
            <v-btn class="modal_confirm_btn" @click="submitRate">{{
              $t("BUTTONS.save")
            }}</v-btn>
            <v-btn class="modal_cancel_btn" @click="rateDialog = false">{{
              $t("BUTTONS.cancel")
            }}</v-btn>
            <v-spacer></v-spacer>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import ChangableMapComponent from "@/components/general/ChangableMapComponent";
import {
  formatScheduleTime,
  formatTimeToHi,
  getBreakEndTime,
  getBreakStartTime,
  getWorkEndTime,
  getWorkStartTime,
} from "@/utils/taskScheduleUtils";

export default {
  name: "ShowTask",
  components: {
    ChangableMapComponent,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },

  watch: {
    id() {
      this.resetLiveTracking();
    },
  },

  data() {
    return {
      data: {
        name_ar: null,
        name_en: null,
        start_at: null,
        end_at: null,
        instructions: null,
        is_active: true,
        facility: null,
      },
      employeesRows: [],
      attendanceRows: [],
      selectedEmployee: null,
      selectedEmployeeReports: null,
      rateDialog: false,
      reportsDialog: false,
      attendanceDialog: false,
      isWaitingAttendanceRequest: false,
      rateData: {
        points: null,
        comment: null,
      },
      attendanceForm: {
        employee: null,
        date: null,
        status: null,
        check_in: null,
        check_out: null,
      },
      liveGuardsById: {},
      pusherConnectionState: "disconnected",
      subscribedUserIds: [],
    };
  },

  computed: {
    employeeHeaders() {
      return [
        {
          text: this.$t("PLACEHOLDERS.employeeName"),
          value: "name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.workStartTime"),
          value: "work_start_time",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.workEndTime"),
          value: "work_end_time",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.breakStartTime"),
          value: "break_start_time",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.breakEndTime"),
          value: "break_end_time",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.employeeReports"),
          value: "reports",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.rates"),
          value: "rate.comment",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.Clients.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ];
    },

    attendanceHeaders() {
      return [
        {
          text: this.$t("PLACEHOLDERS.employeeName"),
          value: "user.name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.date"),
          value: "date",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.checkInTime"),
          value: "check_in",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.checkOutTime"),
          value: "check_out",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.workStartTime"),
          value: "expected_work_start_time",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.workEndTime"),
          value: "expected_work_end_time",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.breakStartTime"),
          value: "expected_break_start_time",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.breakEndTime"),
          value: "expected_break_end_time",
          align: "center",
          sortable: false,
        },
      ];
    },

    facilityShape() {
      const shape = this.data.facility?.shape;
      if (!Array.isArray(shape)) return [];

      return shape
        .map((point) => ({
          lat: Number(point?.lat),
          long: Number(point?.long),
        }))
        .filter(
          (point) => Number.isFinite(point.lat) && Number.isFinite(point.long)
        );
    },

    facilityMapCenter() {
      if (!this.facilityShape.length) {
        return { lat: 24.7136, lng: 46.6753 };
      }

      const totalLat = this.facilityShape.reduce(
        (sum, point) => sum + point.lat,
        0
      );
      const totalLng = this.facilityShape.reduce(
        (sum, point) => sum + point.long,
        0
      );

      return {
        lat: totalLat / this.facilityShape.length,
        lng: totalLng / this.facilityShape.length,
      };
    },

    attendanceEmployeeOptions() {
      return this.employeesRows.map((employee) => ({
        id: employee.id,
        name: employee.name,
        value: employee.id,
      }));
    },

    attendanceStatuses() {
      return [
        { id: 1, name: this.$t("STATUS.absent"), value: "absent" },
        { id: 2, name: this.$t("STATUS.present"), value: "present" },
        { id: 3, name: this.$t("STATUS.late"), value: "late" },
        {
          id: 4,
          name: this.$t("STATUS.auto_finished"),
          value: "auto_finished",
        },
      ];
    },

    liveMapMarkers() {
      return Object.values(this.liveGuardsById).filter(
        (guard) => Number.isFinite(guard.lat) && Number.isFinite(guard.lng)
      );
    },

    isPusherConnected() {
      return this.pusherConnectionState === "connected";
    },

    connectionStatusLabel() {
      if (this.pusherConnectionState === "connecting") {
        return this.$t("STATUS.connecting");
      }

      return this.isPusherConnected
        ? this.$t("STATUS.connected")
        : this.$t("STATUS.disconnected");
    },
  },

  methods: {
    formatScheduleTime(time) {
      const formatted = formatTimeToHi(time);
      return formatted || this.$t("TABLES.noData");
    },

    formatWorkStartTime(record) {
      return this.formatScheduleTime(getWorkStartTime(record));
    },

    formatWorkEndTime(record) {
      return this.formatScheduleTime(getWorkEndTime(record));
    },

    formatBreakStartTime(record) {
      return formatScheduleTime(getBreakStartTime(record), "-");
    },

    formatBreakEndTime(record) {
      return formatScheduleTime(getBreakEndTime(record), "-");
    },

    getUniqueEmployeesRows(employees) {
      const map = new Map();

      (employees || []).forEach((employee) => {
        if (employee?.id != null) {
          map.set(employee.id, employee);
        }
      });

      return Array.from(map.values());
    },

    async getDataToShow() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `tasks/${this.id}`,
        });
        const task = res.data.data.data;

        this.data.name_ar = task.name_ar || task.name;
        this.data.name_en = task.name_en || task.name;
        this.data.start_at = task.start_at;
        this.data.end_at = task.end_at;
        this.data.instructions = task.instructions;
        this.data.facility = task.facility;
        this.data.is_active = !!(task.is_active ?? 1);
        this.employeesRows = this.getUniqueEmployeesRows(task.employees);
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },

    async getAttendances() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: "attendances",
          params: { taskId: this.id },
        });

        this.attendanceRows = res.data.data || [];
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      }
    },

    closeActiveMultiselects() {
      document
        .querySelectorAll(".attendance_dialog .multiselect--active")
        .forEach((element) => {
          element.classList.remove("multiselect--active");
        });
    },

    getAttendancePopupContainer(trigger) {
      return (
        trigger.closest(".attendance_dialog") ||
        trigger.closest(".v-dialog") ||
        document.body
      );
    },

    resetAttendanceForm() {
      this.attendanceForm = {
        employee: null,
        date: null,
        status: null,
        check_in: null,
        check_out: null,
      };
    },

    openAttendanceModal() {
      this.resetAttendanceForm();
      this.closeActiveMultiselects();
      this.attendanceDialog = true;
    },

    closeAttendanceModal() {
      this.attendanceDialog = false;
      this.resetAttendanceForm();
    },

    async submitAttendanceChange() {
      if (!this.attendanceForm.employee?.value) {
        this.$message.error(this.$t("VALIDATION.employee"));
        return;
      }

      if (!this.attendanceForm.date) {
        this.$message.error(this.$t("VALIDATION.date"));
        return;
      }

      if (!this.attendanceForm.status?.value) {
        this.$message.error(this.$t("VALIDATION.status"));
        return;
      }

      this.isWaitingAttendanceRequest = true;

      try {
        const REQUEST_DATA = new FormData();
        REQUEST_DATA.append("user_id", this.attendanceForm.employee.value);
        REQUEST_DATA.append("status", this.attendanceForm.status.value);
        REQUEST_DATA.append(
          "check_in",
          formatTimeToHi(this.attendanceForm.check_in)
        );
        REQUEST_DATA.append(
          "check_out",
          formatTimeToHi(this.attendanceForm.check_out)
        );
        REQUEST_DATA.append("task_id", this.id);
        REQUEST_DATA.append("date", this.attendanceForm.date);

        await this.$axios({
          method: "POST",
          url: "attendances/change-status",
          data: REQUEST_DATA,
        });

        this.attendanceDialog = false;
        this.isWaitingAttendanceRequest = false;
        this.resetAttendanceForm();
        this.$message.success(this.$t("MESSAGES.addedSuccessfully"));
        await this.getAttendances();
      } catch (error) {
        this.isWaitingAttendanceRequest = false;
        this.$message.error(error.response?.data?.message || error.message);
      }
    },

    async changeEmployeeTaskStatus(employee, status) {
      try {
        const REQUEST_DATA = new FormData();
        REQUEST_DATA.append("status", status);

        await this.$axios({
          method: "POST",
          url: `tasks/${this.id}/change-status/${employee.id}`,
          data: REQUEST_DATA,
        });

        this.$message.success(this.$t("MESSAGES.updatedSuccessfully"));
        this.getDataToShow();
      } catch (error) {
        this.$message.error(error.response?.data?.message || error.message);
      }
    },

    openRateModal(employee) {
      this.selectedEmployee = employee;
      this.rateData.points = null;
      this.rateData.comment = null;
      this.rateDialog = true;
    },

    openReportsModal(employee) {
      this.selectedEmployeeReports = employee;
      this.reportsDialog = true;
    },

    isImageFile(url) {
      if (!url) return false;

      return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url);
    },

    async submitRate() {
      if (!this.selectedEmployee?.id) return;

      try {
        const REQUEST_DATA = new FormData();
        REQUEST_DATA.append("points", this.rateData.points || 0);
        REQUEST_DATA.append("comment", this.rateData.comment || "");

        await this.$axios({
          method: "POST",
          url: `tasks/${this.id}/rate/${this.selectedEmployee.id}`,
          data: REQUEST_DATA,
        });

        this.rateDialog = false;
        this.$message.success(this.$t("MESSAGES.updatedSuccessfully"));
      } catch (error) {
        this.$message.error(error.response?.data?.message || error.message);
      }
    },

    isEmployeeTaskFinished(employee) {
      const status = employee?.status_enum || employee?.status;
      return status === "finished" || status === "auto_finished";
    },

    getPresenceMembers(payload) {
      const data = payload?.data?.data || payload?.data || payload || {};

      if (Array.isArray(data.members)) return data.members;
      if (Array.isArray(data.data?.members)) return data.data.members;
      if (Array.isArray(data)) return data;

      return [];
    },

    parseRealtimePayload(payload) {
      let data = payload;

      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (error) {
          return null;
        }
      }

      if (data?.data && typeof data.data === "object") {
        data = data.data;
      }

      return data || null;
    },

    getGuardGeofenceStatus(isInside) {
      if (isInside === true) return this.$t("PLACEHOLDERS.insideGeofence");
      if (isInside === false) return this.$t("PLACEHOLDERS.outsideGeofence");
      return "";
    },

    isProviderMember(member) {
      return member?.type === "provider";
    },

    upsertLiveGuard(payload) {
      const data = this.parseRealtimePayload(payload);
      if (!data || this.isProviderMember(data)) return;

      const id = data.user_id ?? data.id;
      if (id == null) return;

      const lat = Number(data.lat ?? data.location?.lat);
      const lng = Number(
        data.long ?? data.lng ?? data.location?.long ?? data.location?.lng
      );
      const key = String(id);
      const current = this.liveGuardsById[key] || {};
      const nextLat = Number.isFinite(lat) ? lat : current.lat;
      const nextLng = Number.isFinite(lng) ? lng : current.lng;
      const isInside =
        typeof data.is_inside === "boolean"
          ? data.is_inside
          : current.is_inside;

      this.$set(this.liveGuardsById, key, {
        id,
        name: data.user_name || data.name || current.name || "",
        image: data.image || data.avatar || current.image || "",
        lat: nextLat,
        lng: nextLng,
        is_inside: isInside,
        timestamp: data.timestamp || current.timestamp || null,
        statusText: this.getGuardGeofenceStatus(isInside),
      });
    },

    removePresenceMember(user) {
      const key = String(user?.id ?? user?.user_id ?? "");
      if (key && this.liveGuardsById[key]) {
        this.$delete(this.liveGuardsById, key);
      }
    },

    findEmployeeIndex(userId) {
      return this.employeesRows.findIndex(
        (employee) => String(employee.id) === String(userId)
      );
    },

    patchEmployee(userId, patch) {
      const index = this.findEmployeeIndex(userId);
      if (index === -1) return;

      this.$set(this.employeesRows, index, {
        ...this.employeesRows[index],
        ...patch,
      });
    },

    handleLocationUpdate(payload) {
      this.upsertLiveGuard(payload);
    },

    handleTaskFinished(payload) {
      const data = this.parseRealtimePayload(payload);
      if (!data) return;

      const userId = data.user_id ?? data.id;
      const status = data.status || "finished";
      const statusLabel = this.$te(`STATUS.${status}`)
        ? this.$t(`STATUS.${status}`)
        : status;

      this.patchEmployee(userId, {
        status: statusLabel,
        status_enum: status,
      });
      this.removePresenceMember({ id: userId });
    },

    handleWorkTime(payload) {
      const data = this.parseRealtimePayload(payload);
      if (!data) return;

      this.patchEmployee(data.user_id, {
        work_start_time: data.work_start_time,
        work_end_time: data.work_end_time,
        break_start_time: data.break_start_time,
        break_end_time: data.break_end_time,
        work_time: {
          start_time: data.work_start_time,
          end_time: data.work_end_time,
        },
      });
    },

    handleBreakTime(payload) {
      const data = this.parseRealtimePayload(payload);
      if (!data) return;

      this.patchEmployee(data.user_id, {
        break_start_time: data.break_start_time,
        break_end_time: data.break_end_time,
        break_time: {
          start_time: data.break_start_time,
          end_time: data.break_end_time,
        },
      });
    },

    async getCurrentGuards() {
      try {
        const res = await this.$axios({
          method: "GET",
          url: `tasks/${this.id}/presence`,
        });

        this.getPresenceMembers(res.data).forEach((member) => {
          this.upsertLiveGuard(member);
        });
      } catch (error) {
        console.log(
          "[Presence]",
          error.response?.status,
          error.response?.data?.message || error.message
        );
      }
    },

    handlePusherStateChange(states) {
      this.pusherConnectionState =
        states?.current ||
        window.Echo?.connector?.pusher?.connection?.state ||
        "disconnected";
    },

    bindPusherConnectionState() {
      const connection = window.Echo?.connector?.pusher?.connection;
      if (!connection) return;

      this.pusherConnectionState = connection.state || "disconnected";
      connection.bind("state_change", this.handlePusherStateChange);
    },

    unbindPusherConnectionState() {
      const connection = window.Echo?.connector?.pusher?.connection;
      connection?.unbind("state_change", this.handlePusherStateChange);
    },

    subscribeToLiveTracking() {
      if (!window.Echo || !this.id) return;

      const taskId = this.id;

      window.Echo.join(`task.${taskId}`)
        .here((users) => {
          (users || []).forEach((user) => this.upsertLiveGuard(user));
        })
        .joining((user) => this.upsertLiveGuard(user))
        .leaving((user) => this.removePresenceMember(user))
        .listen(".location.updated", this.handleLocationUpdate)
        .listen(".task.finished", this.handleTaskFinished)
        .error((error) => {
          console.log("[Pusher] presence error", error);
        });

      const employeeIds = this.employeesRows
        .map((employee) => employee.id)
        .filter((id) => id != null);

      this.subscribedUserIds = employeeIds;

      employeeIds.forEach((userId) => {
        window.Echo.private(`task.${taskId}.user.${userId}`)
          .listen(".work_time", this.handleWorkTime)
          .listen(".break_time", this.handleBreakTime)
          .listen(".task.finished", this.handleTaskFinished);
      });
    },

    leaveLiveTracking() {
      if (!window.Echo || !this.id) return;

      window.Echo.leave(`task.${this.id}`);
      this.subscribedUserIds.forEach((userId) => {
        window.Echo.leave(`task.${this.id}.user.${userId}`);
      });
      this.subscribedUserIds = [];
    },

    async resetLiveTracking() {
      this.leaveLiveTracking();
      this.liveGuardsById = {};
      await this.getDataToShow();
      await this.getAttendances();
      await this.getCurrentGuards();
      this.subscribeToLiveTracking();
    },
  },

  async created() {
    this.bindPusherConnectionState();
    await this.getDataToShow();
    await this.getAttendances();
    await this.getCurrentGuards();
    this.subscribeToLiveTracking();
  },

  beforeDestroy() {
    this.leaveLiveTracking();
    this.unbindPusherConnectionState();
  },
};
</script>

<style scoped>
.facility_box {
  background: var(--theme_second_bg_clr);
  border: 1px solid var(--border_clr);
  border-radius: 8px;
  padding: 12px;
}

.facility_box p {
  color: var(--theme_text_clr);
  margin-bottom: 6px;
}

.facility_box p:last-child {
  margin-bottom: 0;
}

.facility_box strong {
  color: var(--main_theme_clr);
}

.live_tracking_map {
  position: relative;
}

.live_connection_badge {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 3;
}

.live_tracking_empty {
  margin-top: 8px;
  color: var(--theme_text_clr);
}

.live_guards_list {
  margin-top: 8px;
}

.actions {
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 8px;
}

.actions button {
  padding: 10px 5px;
  font-size: 18px;
}

.actions button.btn_show i,
.actions button.btn_show svg {
  color: #419344;
}

.actions button.btn_edit i,
.actions button.btn_edit svg {
  color: var(--gold_clr);
}

.actions button.btn_delete i,
.actions button.btn_delete svg {
  color: var(--light_red_clr);
}

.attendance_section_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.add_attendance_btn {
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  background: #446e80;
  color: #fff;
  font-size: 14px;
}

.report_item {
  background: var(--theme_second_bg_clr);
  border: 1px solid var(--border_clr);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
}

.report_item:last-child {
  margin-bottom: 0;
}

.report_meta {
  color: var(--theme_text_clr);
  margin-bottom: 8px;
}

.report_meta:last-of-type {
  margin-bottom: 0;
}

.report_files {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.report_file_link {
  display: inline-block;
}

.report_image {
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--border_clr);
}
</style>

<style>
.attendance_dialog {
  overflow: visible !important;
}

.attendance_dialog .v-card {
  overflow: visible !important;
}

.attendance_dialog form .input_wrapper.top_label.select_wrapper {
  position: relative;
}

.attendance_dialog
  form
  .multiselect:not(.multiselect--active)
  .multiselect__content-wrapper {
  display: none !important;
  pointer-events: none !important;
}

.attendance_dialog form .my-5 {
  margin-top: 0.75rem !important;
  margin-bottom: 0.75rem !important;
}

.attendance_dialog
  form
  .input_wrapper.top_label.select_wrapper
  .multiselect--active {
  position: relative;
  z-index: 10001;
}

.attendance_dialog
  form
  .input_wrapper.top_label.select_wrapper
  .multiselect__content-wrapper {
  z-index: 10002 !important;
}

.attendance_dialog .ant-calendar-picker-container,
.attendance_dialog .ant-time-picker-panel {
  z-index: 10003 !important;
}
</style>
